import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Minus, ShieldCheck, Crown, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card } from "../components/core/Layout";
import { StatusBadge } from "../components/core/Badges";
import { cn } from "../lib/utils";

interface Admin { id: string; name: string; email: string; is_active: boolean; last_login_at?: string | null }
interface Access {
  roles: { key: string; label: string; scope: string; description: string }[];
  modules: { key: string; label: string }[];
  tenants: { id: string; name: string; is_active: boolean; modules_enabled: string[]; admins: Admin[] }[];
  super_admins?: Admin[];
}

const Tick = ({ on, testId }: { on: boolean; testId?: string }) => (
  <span data-testid={testId} className={cn("mx-auto flex h-6 w-6 items-center justify-center rounded-full", on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-300")}>{on ? <Check className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</span>
);

function RolesCard({ roles }: { roles: Access["roles"] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {roles.map((r, i) => (
        <Card key={r.key} testId={`role-card-${r.key}`} className={cn("rise", `rise-${i}`)}>
          <div className="flex items-start gap-3">
            <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", r.key === "super_admin" ? "bg-[#10046E] text-[color:var(--gold)]" : "bg-indigo-50 text-[color:var(--brand)]")}>{r.key === "super_admin" ? <Crown className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}</span>
            <div><p className="font-display text-sm font-semibold text-slate-900">{r.label}</p><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{r.scope}</p><p className="mt-1.5 text-sm text-slate-600">{r.description}</p></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function TenantMatrix({ data }: { data: Access }) {
  const t = data.tenants[0];
  if (!t) return null;
  return (
    <Card title="Module access by administrator" icon={Users} testId="access-matrix-card" className="rise rise-2">
      <p className="mb-4 text-xs text-slate-500">Every active administrator has access to all modules enabled for {t.name}. Modules marked off are not part of your subscription.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm" data-testid="access-matrix-table">
          <thead><tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"><th className="pb-2 pr-4">Module</th><th className="pb-2 pr-4 text-center">Enabled</th>{t.admins.map((a) => <th key={a.id} className="pb-2 px-2 text-center font-semibold normal-case tracking-normal"><p className="truncate max-w-[140px] text-slate-800">{a.name}</p><p className="truncate max-w-[140px] text-[10px] font-normal text-slate-400">{a.email}</p></th>)}</tr></thead>
          <tbody>
            {data.modules.map((m) => { const on = t.modules_enabled.includes(m.key); return (
              <tr key={m.key} className="border-b border-slate-100" data-testid={`access-row-${m.key}`}>
                <td className="py-2.5 pr-4 font-medium text-slate-800">{m.label}</td>
                <td className="py-2.5 pr-4"><Tick on={on} testId={`module-enabled-${m.key}`} /></td>
                {t.admins.map((a) => <td key={a.id} className="py-2.5 px-2"><Tick on={on && a.is_active} /></td>)}
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BoMatrix({ data }: { data: Access }) {
  return (
    <>
      <Card title="Module access by client" icon={Users} testId="access-matrix-card" className="rise rise-2">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="access-matrix-table">
            <thead><tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"><th className="pb-2 pr-4">Client</th><th className="pb-2 pr-4">Status</th><th className="pb-2 pr-4 text-center">Admins</th>{data.modules.map((m) => <th key={m.key} className="pb-2 px-1 text-center">{m.label.split(" ")[0]}</th>)}</tr></thead>
            <tbody>
              {data.tenants.map((t) => (
                <tr key={t.id} className="border-b border-slate-100" data-testid={`access-row-${t.id}`}>
                  <td className="py-2.5 pr-4 font-medium text-slate-800">{t.name}</td>
                  <td className="py-2.5 pr-4"><StatusBadge status={t.is_active ? "active" : "inactive"} /></td>
                  <td className="py-2.5 pr-4 text-center text-slate-700" data-testid={`admin-count-${t.id}`}>{t.admins.filter((a) => a.is_active).length}<span className="text-slate-400">/{t.admins.length}</span></td>
                  {data.modules.map((m) => <td key={m.key} className="py-2.5 px-1"><Tick on={t.modules_enabled.includes(m.key)} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="POMEN super admins" icon={Crown} testId="super-admins-card" className="rise rise-3">
        <ul className="divide-y divide-slate-100">
          {(data.super_admins || []).map((a) => (
            <li key={a.id} className="flex items-center justify-between py-2 text-sm" data-testid={`super-admin-${a.id}`}><div><p className="font-medium text-slate-800">{a.name}</p><p className="text-xs text-slate-500">{a.email}</p></div><StatusBadge status={a.is_active ? "active" : "inactive"} /></li>
          ))}
        </ul>
      </Card>
    </>
  );
}

export function RolesAccessContent() {
  const { user } = useAuth();
  const isBO = user?.user_type === "super_admin";
  const { data } = useQuery<Access>({ queryKey: ["access-matrix"], queryFn: async () => (await api.get("/tenants/access")).data });
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="roles-access-content">
      <RolesCard roles={data.roles} />
      {isBO ? <BoMatrix data={data} /> : <TenantMatrix data={data} />}
    </div>
  );
}

export default function RolesAccessPage() {
  const { user } = useAuth();
  const isBO = user?.user_type === "super_admin";
  return (
    <div data-testid="roles-access-page">
      <PageHeader eyebrow="Settings" title="Roles & Access" subtitle={isBO ? "Who can do what across the platform, and which modules each client has enabled." : "Roles in ENFLEET and which modules your administrators can access."} />
      <RolesAccessContent />
    </div>
  );
}
